import React, { useEffect, useState, Component } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
export function Navbar() {
  const {
    t
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Handle hash scrolling when navigating from another page
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else if (!location.hash && isHomePage) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [location, isHomePage]);
  const navLinks = [{
    name: t.nav.home,
    href: '#hero'
  }, {
    name: t.nav.services,
    href: '#services'
  }, {
    name: t.nav.events,
    href: '#events'
  }, {
    name: t.nav.pilots,
    href: '#pilot-info'
  }, {
    name: t.nav.briefing,
    href: '/briefing',
    highlight: true,
    isRoute: true
  }, {
    name: t.nav.contact,
    href: '#contact'
  }];
  const getLinkProps = (link: (typeof navLinks)[0]) => {
    if (link.isRoute) {
      return {
        to: link.href,
        as: Link
      };
    }
    // If on homepage, use simple anchor for smooth scroll
    if (isHomePage) {
      return {
        href: link.href,
        as: 'a'
      };
    }
    // If on other page, link to homepage with hash
    return {
      to: `/${link.href}`,
      as: Link
    };
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-[#0a1628]/90 backdrop-blur-md border-b border-white/10' : 'bg-transparent'}`} data-id="element-179" data-mp="2wcghqraqzf3e">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-180" data-mp="361q6pyaaohna">
        <div className="flex items-center justify-between h-20" data-id="element-181" data-mp="3642w2widtuad">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold tracking-tighter text-white group" data-id="element-182" data-mp="2icnpgrjkoqtb">
            LKHV
            <span className="text-[#38bdf8] group-hover:text-[#60a5fa] transition-colors" data-id="element-183" data-mp="2sb4mo693u1nt">
              .<span className="text-lg font-semibold" data-id="element-184" data-mp="2nbrzfkebmskp">eu</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-185" data-mp="7jb7tmdmi104">
            {navLinks.map(link => {
            const {
              as: Component,
              ...props
            } = getLinkProps(link);
            return (
              // @ts-ignore - Dynamic component props
              <Component key={link.name} {...props} className={`text-sm font-medium transition-colors ${link.highlight ? 'text-[#38bdf8] hover:text-[#60a5fa]' : 'text-white/80 hover:text-[#38bdf8]'}`} data-id="element-186" data-mp="f3vin8s3qvd4">
                  {link.name}
                </Component>
            );
          })}
            <LanguageSwitcher data-id="element-187" data-mp="svtl50rb5t0b" />
          </div>

          {/* Mobile: language + menu button */}
          <div className="md:hidden flex items-center gap-3" data-id="element-188" data-mp="xtv4znwk5wcu">
            <LanguageSwitcher data-id="element-189" data-mp="k3hy7oveknp3" />
            <button className="text-white hover:text-[#38bdf8] transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-190" data-mp="k2gl7euttr4q">
              {isMobileMenuOpen ? <X size={24} data-id="element-191" data-mp="1gkunc69js7x5" /> : <Menu size={24} data-id="element-192" data-mp="1gkmcsl0wdep4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-193" data-mp="3p2736tbfazfv">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-[#0a1628] border-b border-white/10 overflow-hidden" data-id="element-194" data-mp="3ozko88m7dhnm">
            <div className="px-6 py-8 flex flex-col space-y-4" data-id="element-195" data-mp="bp56ukjf1w0q">
              {navLinks.map(link => {
            const {
              as: Component,
              ...props
            } = getLinkProps(link);
            return (
              // @ts-ignore - Dynamic component props
              <Component key={link.name} {...props} className={`text-lg font-medium transition-colors ${link.highlight ? 'text-[#38bdf8] hover:text-[#60a5fa]' : 'text-white/80 hover:text-[#38bdf8]'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-196" data-mp="3u08m9e8umyrc">
                    {link.name}
                  </Component>
            );
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}